(function () {
    'use strict';

    angular
        .module('salesflare.components.tasks.meetingSidebar', [])
        .component('sfMeetingSidebar', {
            controller,
            controllerAs: 'vm',
            templateUrl: 'app-ajs/components/tasks/meetingSidebar/meetingSidebar.html',
            bindings: {
                meetingTask: '<',
                refreshKey: '<',
                onClose: '&',
                onMeetingUpdated: '&'
            }
        });

    function controller($q, $rootScope, $scope, $state, $mdDialog, $mdMedia, meeting, events, account, accounts, utils, email, helperFunctionsService, model, $translate) {

        const vm = this;
        let latestMeetingRequestId = 0;
        vm.sectionExpanded = {
            notes: false,
            preparation: false,
            description: false
        };
        vm.meeting = null;
        vm.meetingTimeText = '';
        vm.preparationUnavailableMessage = '';
        vm.participantLists = {
            contacts: [],
            users: []
        };
        vm.isPreparing = false;
        vm.isLoading = false;
        vm.isCreatingAccount = false;
        vm.$mdMedia = $mdMedia;
        vm.insufficientPermissionsMessage = $translate.instant('You don\'t have permission to edit this meeting');

        const preparationUnavailableMessages = {
            'no_external_participants': $translate.instant('Can\'t prepare because there\'s no external participants.'),
            'ended': $translate.instant('Can\'t prepare because the meeting has already ended.'),
            'multiple_accounts': $translate.instant('Can\'t prepare because it\'s in multiple accounts.')
        };

        const prepareStatusMessages = {
            ...preparationUnavailableMessages,
            'not_needed': $translate.instant('Preparation is already up to date.')
        };

        vm.$onInit = function () {
            if (vm.meetingTask?.meeting?.id) {
                return loadMeeting();
            }
        };

        vm.$onChanges = function (changes) {
            if (
                changes.meetingTask
                && changes.meetingTask.currentValue?.meeting?.id
                && changes.meetingTask.currentValue?.meeting?.id !== changes.meetingTask.previousValue?.meeting?.id
            ) {
                vm.sectionExpanded = {
                    notes: false,
                    preparation: false,
                    description: false
                };
                vm.isPreparing = false;
                resetMeetingState();
                return loadMeeting();
            }

            if (changes.refreshKey && !changes.refreshKey.isFirstChange() && vm.meetingTask?.meeting?.id) {
                return loadMeeting();
            }
        };

        vm.canAct = function () {

            return !vm.isLoading && isSelectedMeeting(vm.meeting?.id);
        };

        // GET /meetings/{id} doesn't return can_edit, so the task's flag restricts editing, same as the task row.
        vm.lacksEditPermission = function () {

            return vm.meetingTask?.can_edit === false;
        };

        vm.canEdit = function () {

            return vm.canAct() && !vm.lacksEditPermission();
        };

        vm.close = function () {
            return vm.onClose();
        };

        vm.openAccount = function () {
            if (!vm.primaryAccount?.id) {
                return;
            }

            return $state.go('accounts.account.feed', { id: vm.primaryAccount.id, name: vm.primaryAccount.name });
        };

        vm.openParticipant = function (participant) {
            if (!participant?.id) {
                return;
            }

            if (participant.type === 'user') {
                return $state.go('user', { id: participant.id });
            }

            return $state.go('contact', { id: participant.id });
        };

        vm.toggleSection = function (section) {
            vm.sectionExpanded[section] = !vm.sectionExpanded[section];
        };

        vm.getSectionText = function (section) {
            return vm.sectionExpanded[section] ? $translate.instant('Show less') : $translate.instant('Show more');
        };

        vm.hasMoreContent = function (html) {
            return stripHtml(html).length > 220;
        };

        vm.getParticipantEmail = function (participant) {
            if (participant.email) {
                return participant.email;
            }

            if (participant.email_addresses?.length > 0) {
                return participant.email_addresses[0].email;
            }

            return '';
        };

        vm.getParticipantPhone = function (participant) {
            if (participant.phone_number) {
                return participant.phone_number;
            }

            if (participant.phone) {
                return participant.phone;
            }

            if (participant.phone_numbers?.length > 0) {
                return participant.phone_numbers[0].number;
            }

            return '';
        };

        vm.editMeeting = function () {
            if (!vm.canEdit()) {
                return;
            }

            const meetingId = vm.meeting.id;
            const accountId = vm.primaryAccount?.id || null;
            const meetingType = vm.meetingTask?.type === 'meeting-phone' ? 'meeting-phone' : 'meeting-live';

            if (!$mdMedia('gt-sm')) {
                return $state.go('meeting', { id: meetingId, type: meetingType, account: accountId });
            }

            return $mdDialog.show({
                clickOutsideToClose: false,
                escapeToClose: false,
                templateUrl: 'partials/meeting.html',
                controller: 'MeetingController',
                onComplete,
                locals: {
                    $stateParams: {
                        id: meetingId,
                        type: meetingType,
                        account: accountId
                    }
                }
            }).then(function (updatedMeeting) {
                if (!updatedMeeting || !isSelectedMeeting(meetingId)) {
                    return;
                }

                return applyUpdatedMeeting(updatedMeeting, meetingId);
            });
        };

        vm.prepareMeeting = function () {

            if (!vm.canEdit() || vm.isPreparing || vm.preparationUnavailableMessage) {
                return;
            }

            const meetingId = vm.meeting.id;
            vm.isPreparing = true;

            return meeting.prepare(meetingId).then(function (response) {
                if (vm.meeting?.id !== meetingId) {
                    return;
                }

                vm.meeting.preparation = response.data.preparation;
                vm.meeting.preparation_date = response.data.preparation_date;

                if (preparationUnavailableMessages[response.data.status]) {
                    vm.meeting.preparation_unavailable_reason = response.data.status;
                }

                vm.preparationUnavailableMessage = getPreparationUnavailableMessage(vm.meeting);

                const statusMessage = prepareStatusMessages[response.data.status];
                if (statusMessage) {
                    utils.showInfoToast(statusMessage);
                }
            }).catch(angular.noop).finally(function () {
                if (vm.meeting?.id === meetingId) {
                    vm.isPreparing = false;
                }
            });
        };

        vm.createAccount = function () {
            if (!vm.canAct() || vm.isCreatingAccount) {
                return;
            }

            const meetingId = vm.meeting.id;
            const contacts = getExternalContacts();

            if (contacts.length === 0) {
                return utils.showErrorToast($translate.instant('No contacts found to create an account for.'));
            }

            // eslint-disable-next-line unicorn/prefer-object-from-entries
            const contactsByDomain = contacts.reduce((result, participant) => {
                const domain = getEmailDomain(participant.email);
                if (!domain) {
                    return result;
                }

                if (!result[domain]) {
                    result[domain] = [];
                }

                result[domain].push(participant);
                return result;
            }, {});

            const candidateDomains = Object.keys(contactsByDomain);
            if (candidateDomains.length === 0) {
                return utils.showErrorToast($translate.instant('No company domains found for the participants.'));
            }

            vm.isCreatingAccount = true;

            const loadDomainOptions = candidateDomains.map((domain) => {
                return accounts.getRelated(null, domain, null).then(function (response) {
                    return mapRelatedResponseToCompanyOptions(response, domain, contactsByDomain[domain]);
                }).catch(function () {
                    return [buildFallbackCompanyOption(domain, contactsByDomain[domain])];
                });
            });

            return $q.all(loadDomainOptions).then(function (domainOptionsPerDomain) {
                if (!isSelectedMeeting(meetingId)) {
                    return { cancelled: true };
                }

                const domainOptions = domainOptionsPerDomain
                    .flat()
                    .filter(Boolean);

                if (domainOptions.length === 0) {
                    return utils.showErrorToast($translate.instant('No company domains found for the participants.'));
                }

                let selectedDomainOption;

                if (domainOptions.length === 1) {
                    [selectedDomainOption] = domainOptions;
                    return selectedDomainOption;
                }

                const companyDialogScope = $scope.$new();
                companyDialogScope.companyOptions = domainOptions;
                companyDialogScope.selectCompany = function (companyOption) {
                    $mdDialog.hide(companyOption);
                };

                return $mdDialog.show({
                    clickOutsideToClose: true,
                    scope: companyDialogScope,
                    preserveScope: false,
                    templateUrl: 'partials/selectcompanydialog.html'
                }).catch(function () {
                    return { cancelled: true };
                });
            }).then(function (selectedDomainOption) {
                if (selectedDomainOption?.cancelled || !selectedDomainOption?.domain || !isSelectedMeeting(meetingId)) {
                    return;
                }

                const sourceDomain = selectedDomainOption.sourceDomain || selectedDomainOption.domain;
                const contactsForDomain = contactsByDomain[sourceDomain] || [];
                const payloadContacts = contactsForDomain.map(function (participant) {
                    return {
                        id: participant.id,
                        name: participant.name,
                        email: participant.email
                    };
                });

                return accounts.get(null, { domain: angular.toJson([selectedDomainOption.domain]) }, 100000, 0, null).then(function (accountsResponse) {
                    if (!isSelectedMeeting(meetingId)) {
                        return;
                    }

                    const existingAccounts = accountsResponse.data || [];

                    if (existingAccounts.length === 0) {
                        return account.create(payloadContacts).then(function (createResponse) {
                            return showAccountToaster(createResponse.data, meetingId);
                        });
                    }

                    const accountDialogScope = $scope.$new();
                    accountDialogScope.availableAccounts = existingAccounts;
                    accountDialogScope.selectAccount = function (selectedAccount) {
                        $mdDialog.hide(selectedAccount);
                    };

                    accountDialogScope.createNew = function () {
                        $mdDialog.hide(null);
                    };

                    return $mdDialog.show({
                        clickOutsideToClose: true,
                        scope: accountDialogScope,
                        preserveScope: false,
                        templateUrl: 'partials/accountalreadyexistsdialog.html'
                    }).catch(function () {
                        return { cancelled: true };
                    }).then(function (selectedAccount) {
                        if (selectedAccount?.cancelled) {
                            return;
                        }

                        if (!selectedAccount) {
                            return account.create(payloadContacts).then(function (createResponse) {
                                return showAccountToaster(createResponse.data, meetingId);
                            });
                        }

                        return account.createContacts(selectedAccount.id, payloadContacts).then(function () {
                            return showAccountToaster(selectedAccount, meetingId);
                        });
                    });
                });
            }).catch(function () {
                vm.isCreatingAccount = false;
                return utils.showErrorToast();
            }).finally(function () {
                vm.isCreatingAccount = false;
            });
        };

        vm.mailto = email.mailto;

        vm.callPerson = function (person, $event) {

            return helperFunctionsService.callPerson(person, vm.getParticipantPhone(person), $event).then(function () {

                return $rootScope.$broadcast(events.account.updated);
            });
        };

        function loadMeeting() {
            if (!vm.meetingTask?.meeting?.id) {
                resetMeetingState();
                return;
            }

            const requestId = ++latestMeetingRequestId;
            const requestedMeetingId = vm.meetingTask.meeting.id;
            vm.isLoading = true;

            return meeting.get(requestedMeetingId, true).then(function (response) {
                if (requestId !== latestMeetingRequestId || !isSelectedMeeting(requestedMeetingId)) {
                    return;
                }

                if (!response.data?.id) {
                    return resetMeetingState();
                }

                vm.meeting = response.data;
                vm.primaryAccount = vm.meeting.accounts?.[0] || null;
                vm.meetingTimeText = getMeetingTimeText(vm.meeting);
                vm.participantLists = getSortedParticipants(getUniqueParticipants(vm.meeting.participants));
                vm.hasExternalParticipants = getExternalContacts().length > 0;
                vm.preparationUnavailableMessage = getPreparationUnavailableMessage(vm.meeting);
            }).catch(function () {
                if (requestId !== latestMeetingRequestId) {
                    return;
                }

                resetMeetingState();
            }).finally(function () {
                if (requestId === latestMeetingRequestId) {
                    vm.isLoading = false;
                }
            });
        }

        function resetMeetingState() {

            vm.meeting = null;
            vm.primaryAccount = null;
            vm.meetingTimeText = '';
            vm.participantLists = {
                contacts: [],
                users: []
            };
            vm.hasExternalParticipants = false;
            vm.preparationUnavailableMessage = '';
        }

        function getPreparationUnavailableMessage(meetingEntity) {

            if (!meetingEntity || meetingEntity.preparation) {
                return '';
            }

            return preparationUnavailableMessages[meetingEntity.preparation_unavailable_reason] || '';
        }

        function isSelectedMeeting(meetingId) {

            return !!meetingId && meetingId === vm.meetingTask?.meeting?.id;
        }

        function getUniqueParticipants(participants) {

            const seenParticipantKeys = new Set();

            return (participants || []).filter(function (participant) {
                const participantKey = participant.type + '-' + participant.id;
                if (seenParticipantKeys.has(participantKey)) {
                    return false;
                }

                seenParticipantKeys.add(participantKey);
                return true;
            });
        }

        function applyUpdatedMeeting(updatedMeeting, meetingId) {

            const originalTask = vm.meetingTask;

            if (updatedMeeting.deleted) {
                const deletedTask = transformMeetingToTask(updatedMeeting, originalTask);
                resetMeetingState();

                return vm.onMeetingUpdated({
                    $event: {
                        task: deletedTask
                    }
                });
            }

            vm.meeting = updatedMeeting;

            return loadMeeting().finally(function () {
                const reloadedMeeting = isSelectedMeeting(meetingId) && vm.meeting?.id === meetingId ? vm.meeting : null;

                vm.onMeetingUpdated({
                    $event: {
                        task: transformMeetingToTask(reloadedMeeting || updatedMeeting, originalTask)
                    }
                });
            });
        }

        function getMeetingTimeText(meetingEntity) {
            const timeZone = getPreferredTimeZone();
            const startDate = moment.tz(meetingEntity.date, timeZone);
            const endDate = moment.tz(meetingEntity.end_date || meetingEntity.date, timeZone);
            const sameDay = startDate.isSame(endDate, 'day');

            const startDateLabel = getRelativeOrShortDateLabel(startDate);
            const endDateLabel = getRelativeOrShortDateLabel(endDate);
            const startTime = formatMeetingTime(startDate);
            const endTime = formatMeetingTime(endDate);

            if (sameDay) {
                return $translate.instant('{{date}} · {{start}} - {{end}}', {
                    date: startDateLabel,
                    start: startTime,
                    end: endTime
                });
            }

            return $translate.instant('{{startDate}} {{startTime}} - {{endDate}} {{endTime}}', {
                startDate: startDateLabel,
                startTime,
                endDate: endDateLabel,
                endTime
            });
        }

        function formatMeetingTime(timeMoment) {
            return timeMoment.format(model.me?.am_pm_notation ? 'h:mm A' : 'HH:mm');
        }

        function getRelativeOrShortDateLabel(dateMoment) {
            const now = moment().tz(getPreferredTimeZone());
            const dayDifference = dateMoment.clone().startOf('day').diff(now.clone().startOf('day'), 'days');

            if (dayDifference === 0) {
                return $translate.instant('Today');
            }

            if (dayDifference === 1) {
                return $translate.instant('Tomorrow');
            }

            if (dateMoment.year() === now.year()) {
                return dateMoment.format('D MMM');
            }

            return dateMoment.format('D MMM YYYY');
        }

        function getPreferredTimeZone() {
            return model.me?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone || model.me?.team?.time_zone || 'UTC';
        }

        function getSortedParticipants(participants) {
            const contacts = participants
                .filter(function (participant) {
                    return participant.type !== 'user';
                })
                .sort(function (participantA, participantB) {
                    return participantA.name.localeCompare(participantB.name);
                });

            const users = participants
                .filter(function (participant) {
                    return participant.type === 'user';
                })
                .sort(function (participantA, participantB) {
                    return participantA.name.localeCompare(participantB.name);
                });

            return { contacts, users };
        }

        function getExternalContacts() {
            return getUniqueParticipants(vm.meeting?.participants).filter(function (participant) {
                if (participant.type === 'user') {
                    return false;
                }

                const e = vm.getParticipantEmail(participant);
                const domain = getEmailDomain(e);

                return !!domain && domain !== model.me.team.domain;
            }).map(function (participant) {
                return {
                    id: participant.id,
                    name: participant.name,
                    email: vm.getParticipantEmail(participant),
                    picture: participant.picture
                };
            });
        }

        function getEmailDomain(e) {
            if (!e || !e.includes('@')) {
                return '';
            }

            return e.split('@')[1].toLowerCase();
        }

        function mapRelatedResponseToCompanyOptions(relatedResponse, domain, contactsForDomain) {

            if (relatedResponse?.data === 'team_black_list_domain' || relatedResponse?.data === 'black_list_domain') {
                return [];
            }

            const fallbackOption = buildFallbackCompanyOption(domain, contactsForDomain);

            if (!relatedResponse || angular.isString(relatedResponse.data) || !angular.isArray(relatedResponse.data) || relatedResponse.data.length === 0) {
                return [fallbackOption];
            }

            const relatedCompanies = relatedResponse.data.filter(function (company) {
                return !!company && angular.isObject(company);
            });

            if (relatedCompanies.length === 0) {
                return [fallbackOption];
            }

            return relatedCompanies.map(function (company) {
                return {
                    id: company.id,
                    sourceDomain: domain,
                    domain: company.domain || domain,
                    name: company.name || domain,
                    picture: company.picture || fallbackOption.picture
                };
            });
        }

        function buildFallbackCompanyOption(domain, contactsForDomain) {
            const fallbackPicture = (contactsForDomain || []).find(function (contact) {
                return !!contact.picture;
            })?.picture;

            return {
                id: domain,
                sourceDomain: domain,
                domain,
                name: domain,
                picture: fallbackPicture
            };
        }

        function showAccountToaster(acc, meetingId) {
            return account.get(acc.id).then(function (response) {
                if (isSelectedMeeting(meetingId)) {
                    vm.primaryAccount = response.data;
                }

                // Not returned so the create flow can settle without waiting for the toast to close.
                utils.showSuccessToast($translate.instant('The account has been created.'), 'VIEW', 10000).then(function (toastResponse) {
                    if (toastResponse === 'VIEW') {
                        return $state.go('accounts.account.feed', { id: acc.id }, { reload: false });
                    }

                    if (isSelectedMeeting(meetingId)) {
                        return loadMeeting();
                    }
                });
            });
        }

        function stripHtml(html) {
            if (!html) {
                return '';
            }

            return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
        }

        function transformMeetingToTask(meetingEntity, originalTask) {
            const transformedTask = {
                type: originalTask.type,
                selected: false,
                id: originalTask.id,
                completed: originalTask.completed,
                completion_date: originalTask.completion_date,
                can_edit: originalTask.can_edit
            };

            if (meetingEntity.deleted) {
                transformedTask.deleted = true;
            }

            transformedTask.meeting = {
                id: meetingEntity.id,
                subject: meetingEntity.subject,
                conference_url: meetingEntity.conference_url
            };

            if (meetingEntity.accounts?.length > 0) {
                transformedTask.account = meetingEntity.accounts[0];
            }

            if (meetingEntity.subject) {
                if (originalTask.type === 'meeting') {
                    transformedTask.description = meetingEntity.subject;
                    transformedTask.plainDescription = meetingEntity.subject;
                }
                else {
                    transformedTask.description = $translate.instant('Add notes to {{subject}}', { subject: meetingEntity.subject });
                    transformedTask.plainDescription = $translate.instant('Add notes to {{subject}}', { subject: meetingEntity.subject });
                }
            }

            if (meetingEntity.participants) {
                transformedTask.assignees = meetingEntity.participants.filter(function (participant) {
                    return participant.type === 'user';
                });
            }

            if (originalTask.type === 'meeting' && meetingEntity.date) {
                transformedTask.reminder_date = meetingEntity.date;
            }
            else if (originalTask.type === 'meeting_notes' && meetingEntity.end_date) {
                transformedTask.reminder_date = meetingEntity.end_date;
            }

            return transformedTask;
        }

        function onComplete(scope, element) {
            exitDialogOnEscape(scope, element);
            exitDialogOnClickOutside(scope, element);
        }

        function exitDialogOnEscape(scope, element) {
            function keyHandlerFn(ev) {
                if (ev.keyCode === 27) {
                    ev.stopPropagation();
                    ev.preventDefault();
                    return scope.back();
                }
            }

            element.on('keydown', keyHandlerFn);
        }

        function exitDialogOnClickOutside(scope, element) {
            let sourceElem;

            function mousedownHandler(ev) {
                sourceElem = ev.target;
            }

            function mouseupHandler(ev) {
                if (sourceElem === element[0] && ev.target === element[0]) {
                    ev.stopPropagation();
                    ev.preventDefault();
                    return scope.back();
                }
            }

            element.on('mousedown', mousedownHandler);
            element.on('mouseup', mouseupHandler);
        }

        $scope.$on('refreshData', function () {
            return loadMeeting();
        });
    }
})();
